import type { FlightCardSummaryComponentProps } from "@b2bportal/core-flights";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { Tooltip } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightTime } from "@tenant/disruption";
import { AirlineIcon, Icon, usePriceDisplay } from "@tenant/ui";
import clsx from "clsx";
import { useNearbyAirports } from "../DisruptionExerciseShopFilter/NearbyAirportsContext";
import { NearbyAirportLabel } from "../NearbyAirportLabel";
import defaultStyles from "./FlightCardSummary.module.scss";

export const FlightCardSummary = ({
  duration,
  originCode,
  destinationCode,
  departureTime,
  arrivalTime,
  primaryCarrier,
  airlineName,
  additionalAirlines,
  flightStops,
  className,
  fareName,
  currentPrice,
  brandName,
}: FlightCardSummaryComponentProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();

  const styles = useFlightStyles(
    CoreFlightsComponent.FlightCardSummary,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.FlightCardSummary
  );

  const { rewardsThenPriceText } = usePriceDisplay({
    prices: currentPrice,
  });

  const nearbyCtx = useNearbyAirports();
  const isNearbyAirport = nearbyCtx.isNearbyAirport(destinationCode);

  const destinationDisplay = isNearbyAirport ? (
    <Tooltip
      content={t("core-disruption.nearbyAirportTooltip", {
        newAirport: destinationCode,
        originalAirport: nearbyCtx.destinationAirportCode,
      })}
    >
      <NearbyAirportLabel>
        {destinationCode}
        <Icon
          iconName={IconNameEnum.infoFilled}
          className={cn("nearby-info-icon")}
        />
      </NearbyAirportLabel>
    </Tooltip>
  ) : (
    destinationCode
  );

  return isDesktopAndUp ? (
    // Desktop Flight Card Summary
    <div className={clsx(block, className)}>
      <div className={cn("icon")}>
        <AirlineIcon
          airlineCode={primaryCarrier}
          altText={airlineName}
          className={cn("airline-icon")}
        />
      </div>
      <div className={cn("content")}>
        <FlightTime
          departureTime={departureTime}
          arrivalTime={arrivalTime}
          stops={flightStops}
        />
        <div className={cn("airports")}>
          {originCode} – {destinationDisplay}
        </div>

        <div className={cn("airline")}>
          {airlineName}
          {(additionalAirlines ?? 0) > 0 ? (
            <span className={cn("additional-airlines")}>
              {" "}
              {t("core-flights.vi.countOther", { count: additionalAirlines })}
            </span>
          ) : null}
        </div>

        <div className={cn("duration")}>
          <Icon iconName={IconNameEnum.timer} />
          {duration}
        </div>

        <div className={cn("stops")}>
          {brandName && `${brandName} - `}{" "}
          {flightStops === 0
            ? t("stopDetails.nonstop")
            : t("stopDetails.stop", { count: flightStops })}
        </div>
      </div>
    </div>
  ) : (
    // Mobile Flight Card Summary
    <>
      <div className={clsx(block, className)}>
        <div className={cn("content")}>
          <div className={clsx(cn("price"))}>
            {t(
              "core-disruption.disruptionUniversalExercise.rebook.flightCheckoutPage.free"
            )}
          </div>
          <FlightTime
            departureTime={departureTime}
            arrivalTime={arrivalTime}
            stops={flightStops}
          />
          <p className={cn("fare-name-with-airports")}>
            {originCode} – {destinationDisplay}
          </p>

          <div className={cn("airline")}>
            <AirlineIcon
              airlineCode={primaryCarrier}
              altText={airlineName}
              className={cn("airline-icon")}
            />
            {airlineName}
            {(additionalAirlines ?? 0) > 0 ? (
              <span className={cn("additional-airlines")}>
                {" "}
                {t("core-flights.vi.countOther", { count: additionalAirlines })}
              </span>
            ) : null}
          </div>

          <p className={cn("duration")}>
            <Icon iconName={IconNameEnum.timer} />
            {duration}
          </p>
          <p className={cn("stops")}>
            {brandName ? `${brandName} - ` : ""}
            {flightStops === 0
              ? t("stopDetails.nonstop")
              : t("stopDetails.stop", { count: flightStops })}
          </p>
        </div>
      </div>
    </>
  );
};
